import type { UserRole } from "@prisma/client";

// ─────────────────────────────────────────────────────────────
// Role Categories
// ─────────────────────────────────────────────────────────────

// Role categories map to jewelry boutique structure:
//   Leadership     -> OWNER, GENERAL_MANAGER, MANAGER, SHIFT_LEAD
//   Sales Floor    -> SERVER (Sales Associate), BARTENDER (Senior Associate), HOST (Greeter), CASHIER
//   Workshop       -> KITCHEN_MANAGER (Workshop Lead), LINE_COOK (Bench Jeweler), EXPO (QC), BUSSER (Polisher)
//   Operations     -> DELIVERY_DRIVER (Courier / Security Transport)
//   Platform       -> PLATFORM_ADMIN
const MANAGEMENT_ROLES: UserRole[] = ["OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD"];
const FRONT_OF_HOUSE_ROLES: UserRole[] = ["SERVER", "BARTENDER", "HOST", "CASHIER"];
const BACK_OF_HOUSE_ROLES: UserRole[] = ["KITCHEN_MANAGER", "LINE_COOK", "EXPO", "BUSSER"];
const OPERATIONS_ROLES: UserRole[] = ["DELIVERY_DRIVER"];
const PLATFORM_ROLES: UserRole[] = ["PLATFORM_ADMIN"];

// ─────────────────────────────────────────────────────────────
// Role Classification
// ─────────────────────────────────────────────────────────────

export function isManagement(role: UserRole): boolean {
  return MANAGEMENT_ROLES.includes(role);
}

export function isFrontOfHouse(role: UserRole): boolean {
  return FRONT_OF_HOUSE_ROLES.includes(role);
}

export function isBackOfHouse(role: UserRole): boolean {
  return BACK_OF_HOUSE_ROLES.includes(role);
}

export function isOperations(role: UserRole): boolean {
  return OPERATIONS_ROLES.includes(role);
}

export function isPlatformAdmin(role: UserRole): boolean {
  return PLATFORM_ROLES.includes(role);
}

export type RoleCategory = "management" | "front_of_house" | "back_of_house" | "operations" | "platform";

export function getRoleCategory(role: UserRole): RoleCategory {
  if (isPlatformAdmin(role)) return "platform";
  if (isManagement(role)) return "management";
  if (isFrontOfHouse(role)) return "front_of_house";
  if (isBackOfHouse(role)) return "back_of_house";
  return "operations";
}

// ─────────────────────────────────────────────────────────────
// Permission Functions (25+)
// ─────────────────────────────────────────────────────────────

export function canManageMenu(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER"].includes(role);
}

export function canManageOrders(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD", "SERVER", "BARTENDER", "CASHIER"].includes(role);
}

export function canCreateOrders(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD", "SERVER", "BARTENDER", "CASHIER"].includes(role);
}

export function canVoidOrders(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER"].includes(role);
}

export function canApplyDiscounts(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD"].includes(role);
}

export function canProcessPayments(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD", "SERVER", "BARTENDER", "CASHIER"].includes(role);
}

export function canProcessRefunds(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER"].includes(role);
}

export function canAccessKDS(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD", "KITCHEN_MANAGER", "LINE_COOK", "EXPO"].includes(role);
}

export function canManageTables(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD", "HOST"].includes(role);
}

export function canManageReservations(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD", "HOST"].includes(role);
}

export function canManageInventory(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "KITCHEN_MANAGER"].includes(role);
}

export function canManageEmployees(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER"].includes(role);
}

export function canManageSchedule(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD"].includes(role);
}

export function canClockInOut(role: UserRole): boolean {
  return !isPlatformAdmin(role); // All restaurant staff can clock in/out
}

export function canManageCustomers(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD", "SERVER", "BARTENDER", "HOST"].includes(role);
}

export function canManageLoyalty(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER"].includes(role);
}

export function canViewReports(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD"].includes(role);
}

export function canExportReports(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER"].includes(role);
}

export function canManageCashDrawer(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD", "CASHIER"].includes(role);
}

export function canAccessAI(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD"].includes(role);
}

export function canViewObservability(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER"].includes(role);
}

export function canAccessAdmin(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER"].includes(role);
}

export function canManageLocations(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER"].includes(role);
}

export function canManageUsers(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER"].includes(role);
}

export function canAccessDeveloperTools(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER"].includes(role);
}

export function canManageFloorPlan(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER"].includes(role);
}

export function canManageTimeEntries(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "SHIFT_LEAD"].includes(role);
}

export function canViewLaborCost(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER"].includes(role);
}

export function canManageRecipes(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "KITCHEN_MANAGER"].includes(role);
}

export function canViewFoodCost(role: UserRole): boolean {
  return ["PLATFORM_ADMIN", "OWNER", "GENERAL_MANAGER", "MANAGER", "KITCHEN_MANAGER"].includes(role);
}


// ─────────────────────────────────────────────────────────────
// Role Labels
// ─────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<UserRole, string> = {
  OWNER: "Owner",
  GENERAL_MANAGER: "Store Director",
  MANAGER: "Store Manager",
  SHIFT_LEAD: "Team Lead",
  SERVER: "Sales Associate",
  BARTENDER: "Senior Client Advisor",
  HOST: "Greeter",
  CASHIER: "Cashier",
  KITCHEN_MANAGER: "Workshop Manager",
  LINE_COOK: "Bench Jeweler",
  EXPO: "Quality Control",
  BUSSER: "Polisher",
  DELIVERY_DRIVER: "Courier",
  PLATFORM_ADMIN: "Platform Admin",
};

export function getRoleLabel(role: UserRole): string {
  return ROLE_LABELS[role] || role;
}

const ROLE_CATEGORY_LABELS: Record<RoleCategory, string> = {
  management: "Management",
  front_of_house: "Front of House",
  back_of_house: "Back of House",
  operations: "Operations",
  platform: "Platform",
};

export function getRoleCategoryLabel(category: RoleCategory): string {
  return ROLE_CATEGORY_LABELS[category];
}

export function getAllRoles(): UserRole[] {
  return [...MANAGEMENT_ROLES, ...FRONT_OF_HOUSE_ROLES, ...BACK_OF_HOUSE_ROLES, ...OPERATIONS_ROLES, ...PLATFORM_ROLES];
}

export function getRolesByCategory(category: RoleCategory): UserRole[] {
  switch (category) {
    case "management": return MANAGEMENT_ROLES;
    case "front_of_house": return FRONT_OF_HOUSE_ROLES;
    case "back_of_house": return BACK_OF_HOUSE_ROLES;
    case "operations": return OPERATIONS_ROLES;
    case "platform": return PLATFORM_ROLES;
  }
}
